/**
 * Extract video provider details from source URL.
 * https://github.com/radiovisual/get-video-id/blob/master/index.js
 */

export type VideoProviderDetails = {
  url: string
  id: string
  type: 'youtube' | 'vimeo'
}

export const parseVideo = (str : string) : VideoProviderDetails | undefined => {
	if (typeof str !== 'string' || /<iframe/gi.test(str)) {
		return undefined
	}

	// remove surrounding whitespaces or linefeeds
	str = str.trim()

	// remove the '-nocookie' flag from youtube urls
	str = str.replace('-nocookie', '')

	// remove any leading `www.`
	str = str.replace('/www.', '/')

	// Try to handle google redirection uri
	if (/\/\/google/.test(str)) {
		// Find the redirection uri
		let matches = str.match(/url=([^&]+)&/)

		// Decode the found uri and replace current url string - continue with final link
		if (matches) {
			// Javascript can get encoded URI
			str = decodeURIComponent(matches[1])
		}
	}

	if (/youtube|youtu\.be|y2u\.be|i.ytimg\./.test(str)) {
        const id = youtube(str)
		if (id) return {
			id: id,
			type: 'youtube',
			url: str
		}
	} else if (/vimeo/.test(str)) {
        const id = vimeo(str)
		if (id) return {
			id: id,
			type: 'vimeo',
			url: str
		}
	}
  
	return undefined
}

/**
 * Get the vimeo id.
 * @param {string} str - the url from which you want to extract the id
 * @returns {string|undefined}
 */
function vimeo(str: string) {
	if (str.indexOf('#') > -1) {
		str = str.split('#')[0];
	}
	if (str.indexOf('?') > -1 && str.indexOf('clip_id=') === -1) {
		str = str.split('?')[0];
	}

	var id;
	var arr;

	var vimeoPipe = [
		'https?:\/\/vimeo\.com\/[0-9]+$',
		'https?:\/\/player\.vimeo\.com\/video\/[0-9]+$',
		'https?:\/\/vimeo\.com\/channels',
		'groups',
		'album'
	].join('|');

	var vimeoRegex = new RegExp(vimeoPipe, 'gim');

	if (vimeoRegex.test(str)) {
		arr = str.split('/');
		if (arr && arr.length) {
			id = arr.pop();
		}
	} else if (/clip_id=/gim.test(str)) {
		arr = str.split('clip_id=');
		if (arr && arr.length) {
			id = arr[1].split('&')[0];
		}
	}

	return id;
}

/**
 * Get the Youtube Video id.
 * @param {string} str - the url from which you want to extract the id
 * @returns {string|undefined}
 */
function youtube(str: string) {
	// shortcode
	var shortcode = /youtube:\/\/|https?:\/\/youtu\.be\/|http:\/\/y2u\.be\//g;

	if (shortcode.test(str)) {
		var shortcodeid = str.split(shortcode)[1];
		return stripParameters(shortcodeid);
	}

	// /v/ or /vi/
	var inlinev = /\/v\/|\/vi\//g;

	if (inlinev.test(str)) {
		var inlineid = str.split(inlinev)[1];
		return stripParameters(inlineid);
	}

	// v= or vi=
	var parameterv = /v=|vi=/g;

	if (parameterv.test(str)) {
		var arr = str.split(parameterv);
		return arr[1].split('&')[0];
	}

	// v= or vi=
	var parameterwebp = /\/an_webp\//g;

	if (parameterwebp.test(str)) {
		var webp = str.split(parameterwebp)[1];
		return stripParameters(webp);
	}

	// embed
	var embedreg = /\/embed\//g;

	if (embedreg.test(str)) {
		var embedid = str.split(embedreg)[1];
		return stripParameters(embedid);
	}

	// ignore /user/username pattern
	var usernamereg = /\/user\/([a-zA-Z0-9]*)$/g;

	if (usernamereg.test(str)) {
		return undefined;
	}

	// user
	var userreg = /\/user\/(?!.*videos)/g;

	if (userreg.test(str)) {
		var elements = str.split('/');
		return stripParameters(elements.pop() || "");
	}

	// attribution_link
	var attrreg = /\/attribution_link\?.*v%3D([^%&]*)(%26|&|$)/;

	if (attrreg.test(str)) {
        const matches = str.match(attrreg)
		if (matches) return matches[1];
	}

	return undefined;
}

/**
 * Strip away any parameters following `?` or `/`
 * @param str
 * @returns {*}
 */
function stripParameters(str: string) {
	// Split parameters or split folder separator
	if (str.indexOf('?') > -1) {
		return str.split('?')[0];
	} else if (str.indexOf('/') > -1) {
		return str.split('/')[0];
	}
	return str;
}